/**
 * TrialBanner
 *
 * Persistent banner shown at the top of the protected app shell while the
 * user is on a trial subscription. Renders nothing for non-trial users.
 *
 * Behaviour:
 *  - Polls /subscriptions/me/subscription on mount and on `valpha:subscription-changed` events.
 *  - Shows days remaining (or "ends today" / "in grace") with an Upgrade CTA.
 *  - Dismissible across browser sessions for the current trial period, but
 *    non-dismissible during the final 3 days for visibility.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Clock, X, Sparkles } from "lucide-react";
import { getMySubscription } from "../../api/subscriptions";

const DISMISS_KEY_PREFIX = "valpha:trial-banner-dismissed";

export function getTrialBannerDismissKey(sub) {
  const tier = sub?.tier || "trial";
  const endsAt = sub?.trial_ends_at || sub?.current_period_end || "unknown";
  return `${DISMISS_KEY_PREFIX}:${tier}:${endsAt}`;
}

function readDismissed(sub) {
  if (!sub?.is_trial) return false;
  try {
    return localStorage.getItem(getTrialBannerDismissKey(sub)) === "1";
  } catch {
    return false;
  }
}

function persistDismissed(sub) {
  if (!sub?.is_trial) return;
  try {
    localStorage.setItem(getTrialBannerDismissKey(sub), "1");
  } catch {
    // Private browsing or blocked storage: in-memory state still dismisses now.
  }
}

function daysBetween(future) {
  if (!future) return null;
  try {
    const ms = new Date(future).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86_400_000));
  } catch {
    return null;
  }
}

export default function TrialBanner() {
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await getMySubscription();
      const nextSub = data?.status === "none" ? null : data;
      setSub(nextSub);
      setDismissed(readDismissed(nextSub));
    } catch {
      setSub(null);
      setDismissed(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener("valpha:subscription-changed", onChange);
    return () => window.removeEventListener("valpha:subscription-changed", onChange);
  }, [refresh]);

  if (!sub || !sub.is_trial) return null;

  const inGrace = sub.status === "grace";
  const daysLeft = daysBetween(sub.trial_ends_at || sub.current_period_end);
  const isUrgent = inGrace || (typeof daysLeft === "number" && daysLeft <= 3);
  const trialName = sub.tier_name || "trial";

  if (dismissed && !isUrgent) return null;

  const handleDismiss = () => {
    persistDismissed(sub);
    setDismissed(true);
  };

  let label;
  if (inGrace) {
    label = "Your trial has ended — you're in a short grace period.";
  } else if (daysLeft === 0) {
    label = "Your free trial ends today.";
  } else if (daysLeft === 1) {
    label = "Your free trial ends tomorrow.";
  } else if (typeof daysLeft === "number") {
    label = `Your free ${trialName} trial ends in ${daysLeft} days.`;
  } else {
    label = `You're on a free ${trialName} trial.`;
  }

  return (
    <div
      role="status"
      className={`flex items-center justify-between gap-3 px-3 py-1.5 text-xs font-medium border-b ${
        isUrgent
          ? "bg-amber-500/15 border-amber-500/40 text-amber-100"
          : "bg-[#4FA9A1]/10 border-[#4FA9A1]/30 text-[#A6E0DB]"
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Clock className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link
          to="/subscription"
          className="inline-flex items-center gap-1 rounded-md bg-[#4FA9A1] hover:bg-[#3e938b] text-[#0F1826] px-2.5 py-1 text-[11px] font-semibold transition"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Upgrade
        </Link>
        {!isUrgent && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss trial banner"
            className="rounded-md p-1 hover:bg-white/10 transition"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
